import { useCallback, useEffect, useState } from 'react'

import { FormHandles } from '@unform/core'

import Panel from '~/components/Panel'
import { formatMoney } from '~/helpers'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import { BriefingRows } from '~/modules/retailMedia/components/BriefingRows'
import { useCampaignForm } from '~/modules/retailMedia/components/CampaignForm/hooks/useCampaignForm'
import {
  AudienceGender,
  AudienceRegion,
  AudienceSocialClass
} from '~/modules/retailMedia/constants/audienceTargeting'
import {
  asTranslate,
  buildAudienceTargetingLabels,
  EMPTY_VALUE
} from '~/modules/retailMedia/helpers/audienceTargetingSummary'

type ReviewStepFormSnapshot = {
  name?: string
  settings?: { destination_url?: string }
  audienceTargeting?: {
    region?: AudienceRegion[]
    ageRange?: string[]
    socialClass?: AudienceSocialClass[]
    gender?: AudienceGender[]
    interests?: string[]
  }
}

type ReviewStepProps = {
  formRef: React.RefObject<FormHandles>
  bannersToTable: { ad_size_name?: string; ad_size?: string }[]
  // Same frozen snapshot lifted for settings.projections (FR-038) --
  // OffsiteInvestment.tsx's onProjectionsChange, via Banner/index.tsx's
  // `projections` state. Loosely typed the same way that prop chain
  // already is; only `objective`/`budget` are read here (cast below), the
  // rest (impressions, clicks, ...) isn't shown in this panel.
  projections: Record<string, unknown> | null
  // Bumped by Banner/index.tsx on any change anywhere in the form (native
  // DOM "change" bubbling up to the Form element). Re-reads the form
  // snapshot while the panel is already open, so e.g. picking a different
  // social class updates this panel immediately instead of only on the
  // next collapse/expand.
  formVersion: number
  // Opens the panel, reading the form again, once it turns true. The panel
  // starts collapsed.
  initialCollapsibleState?: boolean
}

const joinOrDash = (values?: string[]) =>
  values?.length ? values.join(', ') : EMPTY_VALUE

/**
 * Read-only creation review (FR-023 to FR-023c): 12 rows, identical between
 * DV360 and Meta except the channel value, shown right before submitting.
 * The submission itself remains BannerFields' own submit button — this
 * panel is display-only (FR-023b/c: this isn't the approver-facing review
 * from 005-offsite-campaign-approval).
 *
 * The snapshot is captured via `formRef.getData()` (the same imperative
 * pattern already used by DigitalSignageConfirmationModal /
 * ObjectiveBasedConfirmationModal for pre-submit summaries in this
 * codebase — unform is ref-based, not reactive, so there's no cheaper way
 * to read fields outside this component without lifting them all into
 * context) — refreshed both when the panel is opened AND, while it's
 * already open, on every `formVersion` bump from Banner/index.tsx (native
 * DOM "change" event bubbling up from anywhere in the form). Without the
 * latter, editing e.g. social class while this panel stayed open needed a
 * collapse/expand to show up here (reported 2026-09-01) — the operator can
 * freely revisit and edit any section above on this single-page form
 * (FR-005), and this panel now reflects that live instead of only at the
 * moment it was last opened.
 *
 * Objective and investment come from `projections` (the same frozen
 * snapshot lifted for settings.projections, FR-038), not the form
 * snapshot: `investmentObjectiveType`/`total_budget` are real unform
 * fields, but `projections` is already the exact validated value
 * (post-debounce, post-minimum-check) and reading it avoids a second,
 * slightly different parsing of the same two fields. These two rows
 * showed "Available in an upcoming release" until Fase 5 (US4) existed —
 * it does now.
 */
const ReviewStep: React.FC<ReviewStepProps> = ({
  formRef,
  bannersToTable,
  projections,
  formVersion,
  initialCollapsibleState = false
}) => {
  const { t } = useAPPTranslation()
  const {
    selectedChannel,
    selectedSubpublishers,
    allSubpublishers,
    interestsEnabled,
    audienceImportEnabled
  } = useCampaignForm()

  const [snapshot, setSnapshot] = useState<ReviewStepFormSnapshot>({})
  const [isOpen, setIsOpen] = useState(false)

  const readSnapshot = useCallback(() => {
    setSnapshot((formRef?.current?.getData() as ReviewStepFormSnapshot) || {})
  }, [formRef])

  const handleCollapse = useCallback(
    (open: boolean) => {
      setIsOpen(open)
      if (open) readSnapshot()
    },
    [readSnapshot]
  )

  // The panel opens by prop here, which never goes through onCollapse, so the
  // snapshot has to be read on this path too.
  useEffect(() => {
    if (initialCollapsibleState) handleCollapse(true)
  }, [initialCollapsibleState, handleCollapse])

  // Re-reads the snapshot while the panel is already open, on any change
  // elsewhere in the form (Banner/index.tsx's `formVersion`) -- otherwise
  // this panel only ever reflected the form as it was at the moment it was
  // last opened, requiring a collapse/expand to see an edit made while it
  // stayed open (reported 2026-09-01).
  useEffect(() => {
    if (isOpen) readSnapshot()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formVersion])

  const destinationRetailerNames = selectedSubpublishers
    ?.map(id => allSubpublishers?.find(item => item.id === id)?.name)
    .filter(Boolean)

  const channelLabel = selectedChannel
    ? t(`rm:campaign.offsite.channel.${selectedChannel}`)
    : EMPTY_VALUE

  const investmentObjective = projections?.objective as
    | InvestmentObjectiveType
    | undefined
  const investmentBudget = projections?.budget as number | undefined

  const objectiveLabel = investmentObjective
    ? t(`rm:campaign.create.InvestmentObjective.${investmentObjective}.label`)
    : EMPTY_VALUE

  const investmentLabel =
    investmentBudget === undefined ? EMPTY_VALUE : formatMoney(investmentBudget)

  // A mesma resolução que a tela de revisão do anunciante usa, para as duas não
  // divergirem no que é "Todas" nem em como um valor vira rótulo.
  const {
    region: regionLabel,
    ageRange: ageRangeLabel,
    socialClass: socialClassLabel,
    gender: genderLabel
  } = buildAudienceTargetingLabels(snapshot?.audienceTargeting, asTranslate(t))

  // Titles, not the individual selected items (FR-017a/c): "Segmentation"
  // names which gates are active, mirroring the prototype's
  // syncMetaReviewSegmentation/syncDv360ReviewSegmentation. `interests`/
  // `audienceImportEnabled` come from context rather than the form
  // snapshot — see the FR-017j note on useCampaignForm.tsx — since the
  // underlying field values persist even after their gate is unmarked.
  //
  // Checks for 'meta' explicitly (mirroring AudienceTargeting's own
  // audienceImportLabel) so the pre-channel-selection state defaults to
  // DV360 here too, instead of showing the Meta label before any channel
  // has been picked.
  const audienceImportTitleKey =
    selectedChannel === 'meta'
      ? 'rm:campaign.create.AudienceTargeting.audienceImport.meta.title'
      : 'rm:campaign.create.AudienceTargeting.audienceImport.dv360.title'

  const segmentationParts = [
    interestsEnabled
      ? t('rm:campaign.create.AudienceTargeting.interests.title')
      : null,
    audienceImportEnabled ? t(audienceImportTitleKey) : null
  ].filter((part): part is string => part !== null)

  const segmentationLabel = segmentationParts.length
    ? segmentationParts.join(' · ')
    : t('rm:campaign.create.ReviewStep.broadAudienceWhenEmpty')

  const creativeSizes = bannersToTable
    ?.map(item => item.ad_size_name || item.ad_size)
    .filter(Boolean)
    .join(', ')

  const creativesCountOptions = {
    count: bannersToTable?.length || 0,
    sizes: creativeSizes
  }

  const creativesLabel = bannersToTable?.length
    ? t('rm:campaign.create.ReviewStep.creativesCount', creativesCountOptions)
    : EMPTY_VALUE

  return (
    <Panel
      title={t('rm:campaign.create.ReviewStep.title')}
      description={t('rm:campaign.create.ReviewStep.description')}
      isCollapsible
      initialCollapsibleState={initialCollapsibleState}
      onCollapse={handleCollapse}
    >
      <BriefingRows
        rows={[
          {
            label: t('rm:campaign.create.ReviewStep.channel'),
            value: channelLabel
          },
          {
            label: t('rm:campaign.create.ReviewStep.name'),
            value: snapshot?.name || EMPTY_VALUE
          },
          {
            label: t('rm:campaign.create.ReviewStep.destinationUrl'),
            value: snapshot?.settings?.destination_url || EMPTY_VALUE
          },
          {
            label: t('rm:campaign.create.ReviewStep.destinationRetailer'),
            value: joinOrDash(destinationRetailerNames)
          },
          {
            label: t('rm:campaign.create.ReviewStep.objective'),
            value: objectiveLabel
          },
          {
            label: t('rm:campaign.create.ReviewStep.investment'),
            value: investmentLabel
          },
          {
            label: t('rm:campaign.create.ReviewStep.region'),
            value: regionLabel
          },
          {
            label: t('rm:campaign.create.ReviewStep.ageRange'),
            value: ageRangeLabel
          },
          {
            label: t('rm:campaign.create.ReviewStep.gender'),
            value: genderLabel
          },
          {
            label: t('rm:campaign.create.ReviewStep.socialClass'),
            value: socialClassLabel
          },
          {
            label: t('rm:campaign.create.ReviewStep.segmentation'),
            value: segmentationLabel
          },
          {
            label: t('rm:campaign.create.ReviewStep.creatives'),
            value: creativesLabel
          }
        ]}
      />
    </Panel>
  )
}

export default ReviewStep
